import FullProfileModalBase from "./FullProfileModalBase";
import React, {Fragment} from "react";
import ShortPassesModal from "./ShortPassesModal";
import ArrowIndicator from "./components/ArrowIndicator";
import GoalMap from "../../../../ui/canvases/GoalMap";
import '../../../../../assets/sass/pages/full-profile/tabs/modals/shots-on-target.scss';
import GoalScoreArea from "../modals/shots_on_target_modal/GoalScoreArea";

import Chart from "react-apexcharts";

const shotsOnTarget = [
    {
        from: {x: 15, y: 300},
        to: {x: -50, y: 620, z: -50},
        type: 'accurate',
        curvature: 100,
        time: 4,
    }, {
        from: {x: -300, y: 300},
        to: {x: 10, y: 620, z: 35},
        type: 'accurate',
        curvature: 50,
        time: 12,

    }, {
        from: {x: 175, y: 325},
        to: {x: 175, y: 620, z: 75},
        type: 'missed',
        curvature: 50,
        time: 35,

    }, {
        from: {x: -5, y: 225},
        to: {x: -200, y: 620, z: 75},
        type: 'missed',
        curvature: -50,
        time: 51,

    }, {
        from: {x: -150, y: 325},
        to: {x: 100, y: 620, z: 60},
        type: 'accurate',
        curvature: 150,
        time: 60,

    }, {
        from: {x: 325, y: 275},
        to: {x: 200, y: 620, z: -20},
        type: 'missed',
        curvature: -50,
        time: 72,

    }, {
        from: {x: -325, y: 275},
        to: {x: -185, y: 620, z: 35},
        type: 'missed',
        curvature: -50,
        time: 87,
    },
];

class ShotsOnTargetModal extends FullProfileModalBase {
    /**
     * Render the components within the body of the modal.
     * @returns {React.ReactNode} The body of the modal.
     */
    renderBody = () => {
        return (
            <Fragment>
                {this.renderArrowIndicator()}
                {this.renderGoalMaps()}
                {this.renderDivider()}
                {this.renderChart()}
            </Fragment>
        );
    };

    /**
     * Renders definitions of arrows in goal maps.
     * @return {React.ReactNode} The arrows definitions.
     */
    renderArrowIndicator = () => {
        return (
            <div className="indicators">
                <ShortPassesModal.ArrowIndicator color={"#2B82F7"} text={"Accurate Shots"}/>
                <ShortPassesModal.ArrowIndicator color={"#F96274"} text={"Missed Shots"}/>
            </div>
        );
    };


    /**
     * Renders the goals map.
     * @return {React.ReactNode} The goal maps component.
     */
    renderGoalMaps = () => {
        return (
            <div className="goal-map-container">
                <GoalScoreArea shotsOnTarget={shotsOnTarget}/>
                <GoalMap shotsOnTarget={shotsOnTarget}/>
            </div>
        );
    };

    /**
     * Renders Chart of shots on target.
     * @returns {React.ReactNode}
     */
    renderChart = () => {
        const chartConfig = this.chartConfig();
        const {options, series} = chartConfig;
        const chartTitle = "Shot Attempted by Interval";

        return (
            <div className="chart-container">
                <hr />
                <div className="title">{chartTitle}</div>
                <Chart options={options} series={series} type="bar" height={220}/>
            </div>
        );
    };


    /**
     * Returns the chart series.
     * @param name {string} The name of series.
     * @param data {object} The y-values of series.
     * @returns {{data: *, name: *}} The cart series.
     * TODO: Complete Doc.
     */
    getSeries = (name, data) => {
        const step = 15;
        const offset = step / 2;

        return {
            name: name,
            data: data.map((value, index) => {
                const key = index * step + offset;
                return [key, value];
            })
        };
    };

    /**
     * Returns the config of chart.
     * @returns {{series: *, options: *}} The chart config.
     */
    chartConfig = () => {
        const types = [{
            name: 'accurate',
            color: '#2B82F7',
        }, {
            name: 'missed',
            color: '#F96274',
        }];

        const shotsConfig = types.map((type) => ({
            ...type,
            data: this.getInterval(type.name),
        }));

        const series = shotsConfig.map((s => this.getSeries(s.name, s.data)));
        const colors = shotsConfig.map((s) => s.color);

        const options = {
                colors: colors,
                legend: {show: false},
                chart: {
                    type: 'bar',
                    width:400,
                    stacked: true,
                    selection: {enabled: false},
                    toolbar: {
                        show: false,
                        tools: {
                            download: false,
                            selection: false,
                            zoom: false,
                        },
                        autoSelected: 'zoom'
                    },
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                        startingShape: 'flat',
                    },
                },
                dataLabels: {enabled: false},
                xaxis: {
                    tickPlacement: 'on',
                    tickAmount: 6,
                    min: 0,
                    max: 90,
                },
                yaxis: {
                    labels: {
                        style: {colors: '#899398', cssClass: 'font-body-sm-medium'},
                        formatter: (value) => Math.round(value),
                    }
                },
                fill: {opacity: 1},
                tooltip: {
                    y: {formatter: (val) => `${val} shot${['', 's'][val === 1 ? 0 : 1]}`}
                }
            }
        ;

        return {
            series: series,
            options: options,
        }
    };

    getInterval = (type) => {
        const shotsOnTarget = this.shotsOnTarget();

        return Array.from(Array(6).keys()).map((index) => {
            const from = index * 15;
            const to = (index + 1) * 15;

            return shotsOnTarget
                .filter((shot) => shot.type === type)
                .filter((shot) => shot.time >= from && shot.time < to).length;
        });
    };

    shotsOnTarget = () => shotsOnTarget;

    /**
     * Returns default size of modal.
     * @returns {string} The modal size.
     */
    defaultSize = () => "lg";

    /**
     * Returns classname of modal.
     * @returns {string} The classname modal.
     */
    modalClassname = () => "shots-on-target-modal";

    static defaultProps = {
        headerTitle: "Shots on Target by Player",
        headerSubtitle: "5 of 10 | 50% Accuracy",
    };
}

ShortPassesModal.ShotsOnTarget = ArrowIndicator;
export default ShotsOnTargetModal;
